import gsap from 'gsap';
import { useEffect, useRef } from 'react';
import { customPixiParticles } from 'custom-pixi-particles';

import { ParticlesController } from '../../../../../../MVCs/Particles/particlesController';
import { isMobile } from '../../../../../../helpers';

const platform = isMobile() ? 'mobile' : 'web';

const boostedWheelParticleSystemConfig = {
  wheelPart: {
    name: 'boostedWheelParticlesWheelPart',
  },
  coinJump: {
    name: 'boostedWheelParticlesCoinJump',
  },
  coinDrop: {
    name: 'boostedWheelParticlesCoinDrop',
  },
};

const boostedWheelParticlesTexturesConfig = {
  wheelPart: [
    `frame/frameModuleResources/boostedJackpot/particles/wheel/${platform}/half_hardDot.png`,
  ],
  coinJump: [
    `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coin1.png`,
    `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coin2.png`,
    `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coin3.png`,
    `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coin4.png`,
  ],
  coinDrop: [
    `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coin1.png`,
    `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coin2.png`,
    `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coin3.png`,
    `frame/frameModuleResources/boostedJackpot/particles/coins/${platform}/coin4.png`,
  ],
};

const boostedWheelParticlesSetup = {
  isStage: false,
  libType: 'custom',
  EmitterClass: customPixiParticles,
  upgradeConfigFn: null,
  systemConfig: boostedWheelParticleSystemConfig,
  texturesConfig: boostedWheelParticlesTexturesConfig,
};

type BoostedJackpotParticlesProps = {
  showCoinCelebration: boolean,
  zoomOutBackground?: boolean,
  wheelParticlesParentRef?: any,
  coinParticlesParentRef?: any,
};

export const BoostedJackpotParticles: React.FC<BoostedJackpotParticlesProps> = ({
  showCoinCelebration,
  zoomOutBackground = false,
  wheelParticlesParentRef,
  coinParticlesParentRef,
}) => {
  const controllerRef = useRef<any>(null);
  const backgroundZoomAnimationRef = useRef<gsap.core.Timeline | null>(null);
  const currentCelebrationStateRef = useRef(showCoinCelebration);
  const backgroundEmitterRef = useRef<any>(null);
  const coinJumpEmitterRef = useRef<any>(null);
  const coinDropEmitterRef = useRef<any>(null);

  currentCelebrationStateRef.current = showCoinCelebration;

  useEffect(() => {
    let isMounted = true;

    const startBackgroundParticles = () => {
      const controller = controllerRef.current;
      const parent = wheelParticlesParentRef?.current;
      if (!controller || !parent) return;

      if (coinJumpEmitterRef.current) controller.stopParticle(coinJumpEmitterRef.current);
      if (coinDropEmitterRef.current) controller.stopParticle(coinDropEmitterRef.current);
      if (backgroundEmitterRef.current) controller.stopParticle(backgroundEmitterRef.current);

      backgroundEmitterRef.current = controller.playParticle('wheelPart', { parent });
    };

    const startCoinCelebration = () => {
      const controller = controllerRef.current;
      const parent = coinParticlesParentRef?.current;
      if (!controller || !parent) return;

      if (backgroundEmitterRef.current) controller.stopParticle(backgroundEmitterRef.current);
      if (coinJumpEmitterRef.current) controller.stopParticle(coinJumpEmitterRef.current);
      if (coinDropEmitterRef.current) controller.stopParticle(coinDropEmitterRef.current);

      coinJumpEmitterRef.current = controller.playParticle('coinJump', { parent });
      coinDropEmitterRef.current = controller.playParticle('coinDrop', { parent });
    };

    const initParticles = async () => {
      const controller = new ParticlesController(boostedWheelParticlesSetup);
      controllerRef.current = controller;
      await controller.init();

      if (!isMounted) {
        controller.destroy();
        controllerRef.current = null;
        return;
      }

      if (currentCelebrationStateRef.current) {
        startCoinCelebration();
        return;
      }

      startBackgroundParticles();
    };

    initParticles();

    return () => {
      isMounted = false;
      backgroundZoomAnimationRef.current?.kill();
      backgroundZoomAnimationRef.current = null;
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const backgroundLayer = wheelParticlesParentRef?.current;
    const backgroundScale = backgroundLayer?.scale;

    backgroundZoomAnimationRef.current?.kill();

    if (!backgroundLayer || !backgroundScale) return;

    if (zoomOutBackground) {
      backgroundZoomAnimationRef.current = gsap.timeline()
        .to(backgroundScale, {
          x: 0,
          y: 0,
          duration: 0.42,
          ease: 'power1.in',
        }, 0)
        .to(backgroundLayer, {
          alpha: 0,
          duration: 0.42,
          ease: 'power1.in',
        }, 0);
      return;
    }

    backgroundLayer.alpha = 1;
    backgroundScale.set(1, 1);
  }, [zoomOutBackground, wheelParticlesParentRef]);

  useEffect(() => {
    if (!showCoinCelebration || !controllerRef.current) return;

    const coinParent = coinParticlesParentRef?.current;
    if (!coinParent) return;

    if (backgroundEmitterRef.current) controllerRef.current.stopParticle(backgroundEmitterRef.current);
    if (coinJumpEmitterRef.current) controllerRef.current.stopParticle(coinJumpEmitterRef.current);
    if (coinDropEmitterRef.current) controllerRef.current.stopParticle(coinDropEmitterRef.current);

    coinJumpEmitterRef.current = controllerRef.current.playParticle('coinJump', { parent: coinParent });
    coinDropEmitterRef.current = controllerRef.current.playParticle('coinDrop', { parent: coinParent });
  }, [coinParticlesParentRef, showCoinCelebration]);

  // Particles render into the slot containers provided via refs - no JSX output needed.
  return null;
};